import * as React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Typography, Icon, Button } from '@mindlercare/ui-web';
import { defaultTheme } from '@mindlercare/ui-web/src/shared/constants';
import type { Message, ExternalSources } from '../types';
import { useTranslation } from '../translations/TranslationHook';
import { buildPdfUrlWithPage } from '../utils/pdfUrlBuilder';

interface SourcesPanelProps {
    message: Message;
    sources?: ExternalSources;
    onClose: () => void;
}

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const SourcesPanelContainer = styled.div`
    height: 100%;
    width: calc(100% - ${defaultTheme.BORDER_RADIUS.LG});
    background: ${defaultTheme.COLORS.WHITE};
    border: 1px solid ${defaultTheme.COLORS.GRAY_MEDIUM};
    border-radius: ${defaultTheme.BORDER_RADIUS.MD};
    padding-top: ${defaultTheme.BORDER_RADIUS.LG};
    padding-bottom: ${defaultTheme.BORDER_RADIUS.LG};
    margin-left: ${defaultTheme.BORDER_RADIUS.LG};
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 0.2s ease-in-out;
`;

const PanelHeader = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: defaultTheme.SPACING.MD,
    paddingRight: defaultTheme.SPACING.MD,
    background: defaultTheme.COLORS.WHITE,
});

const PanelContent = styled.div({
    flex: 1,
    overflowY: 'auto',
    display: "flex",
    flexDirection: "column",
    gap: defaultTheme.SPACING.MD,
    padding: defaultTheme.SPACING.MD,
});

const SourceItem = styled.a<{ $hasLink: boolean }>(({ $hasLink }) => ({
    padding: defaultTheme.SPACING.MD,
    background: defaultTheme.COLORS.GRAY_X_LIGHT,
    borderRadius: defaultTheme.BORDER_RADIUS.SM,
    transition: 'background 0.2s ease',
    cursor: $hasLink ? 'pointer' : 'default',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    '&:hover': {
        background: $hasLink ? defaultTheme.COLORS.GRAY_LIGHT : defaultTheme.COLORS.WHITE,
    },
}));

const SourcePagesContainer = styled.div({
    marginBottom: defaultTheme.SPACING.SM,
});

const PageSpan = styled.div({
    fontSize: '14px',
    color: defaultTheme.COLORS.GRAY,
    borderRadius: defaultTheme.BORDER_RADIUS.SM,
    display: 'inline-block',
    width: 'fit-content',
});

const TextPreview = styled.div({
    fontSize: '13px',
    color: defaultTheme.COLORS.GRAY_DARK,
    marginTop: defaultTheme.SPACING.XS,
    lineHeight: '1.4',
});

export const SourcesPanel: React.FC<SourcesPanelProps> = ({ message, sources: externalSources, onClose }) => {
    const t = useTranslation();
    const candidates = message.candidates ?? [];
    const firstCandidate = candidates?.[0];
    const groundingMetadata = firstCandidate?.groundingMetadata;
    const groundingChunks = groundingMetadata?.groundingChunks ?? [];
    const groundingSupports = groundingMetadata?.groundingSupports ?? [];


    // Build unique list of grounding chunks that are referenced in groundingSupports
    const sourceEntries = React.useMemo(() => {
        const uniqueChunkIndices = new Set<number>();

        // Collect all unique chunk indices from groundingSupports
        groundingSupports.forEach((support) => {
            support.groundingChunkIndices?.forEach((chunkIndex) => {
                uniqueChunkIndices.add(chunkIndex);
            });
        });

        // Build the source entries from unique chunks
        const entries: Array<{
            uri: string;
            title: string;
            pageSpan?: { firstPage?: number; lastPage?: number };
            textPreview?: string;
            externalUrl?: string;
            updateTime?: string;
        }> = [];

        uniqueChunkIndices.forEach((chunkIndex) => {
            const chunk = groundingChunks[chunkIndex];
            if (!chunk) return;

            const uri = chunk.retrievedContext?.uri || '';
            const title = chunk.retrievedContext?.title || '';
            const pageSpan = chunk.retrievedContext?.ragChunk?.pageSpan;
            const rawText = chunk.retrievedContext?.ragChunk?.text || '';
            // Get first 150 characters and clean up line breaks
            const textPreview = rawText.replace(/\r\n/g, ' ').replace(/\n/g, ' ').trim().substring(0, 150);
            const sourceData = externalSources?.find(s => s.uri === uri);
            let externalUrl = sourceData?.url;
            const updateTime = sourceData?.updateTime;

            // Add page navigation to PDF URLs
            if (externalUrl) {
                const page = pageSpan?.firstPage;
                externalUrl = buildPdfUrlWithPage(externalUrl, page);
            }

            entries.push({
                uri,
                title,
                pageSpan,
                textPreview: textPreview || undefined,
                externalUrl,
                updateTime
            });
        });

        return entries;
    }, [groundingChunks, groundingSupports, externalSources]);

    return (
        <SourcesPanelContainer>
            <PanelHeader>
                <Typography variant="h4">{t.SOURCES_PANEL_TITLE}</Typography>
                <Button variant="neutral" onClick={onClose}>
                    <Icon.Close />
                </Button>
            </PanelHeader>
            <PanelContent>
                {
                    sourceEntries.map((entry, index) => (
                        <SourceItem
                            key={`${entry.uri}-${index}`}
                            href={entry.externalUrl || undefined}
                            target={entry.externalUrl ? '_blank' : undefined}
                            rel={entry.externalUrl ? 'noopener noreferrer' : undefined}
                            $hasLink={!!entry.externalUrl}
                            as={entry.externalUrl ? 'a' : 'div'}
                        >
                            <Typography variant='h5'>{entry.title || entry.uri}</Typography>
                            {entry.pageSpan && (
                                <SourcePagesContainer>
                                    <Typography variant='body-small' color="GRAY" style={{ marginBottom: '4px' }}>
                                        {t.SOURCES_PAGES_LABEL}{': '}
                                    </Typography>
                                    <PageSpan>
                                        {entry.pageSpan.lastPage && entry.pageSpan.lastPage !== entry.pageSpan.firstPage
                                            ? `${entry.pageSpan.firstPage}-${entry.pageSpan.lastPage}`
                                            : `${entry.pageSpan.firstPage}`}
                                    </PageSpan>
                                </SourcePagesContainer>
                            )}
                            {entry.textPreview && (
                                <TextPreview style={{ marginBottom: defaultTheme.SPACING.SM }}>
                                    {entry.textPreview}
                                    {entry.textPreview.length >= 150 ? '...' : ''}
                                </TextPreview>
                            )}
                            {entry.updateTime && (
                                <Typography variant='body-x-small' style={{ color: defaultTheme.COLORS.GRAY }}>
                                    {t.UPDATED_AT}: {new Date(entry.updateTime).toLocaleString()}
                                </Typography>
                            )}
                        </SourceItem>
                    ))
                }
            </PanelContent>
        </SourcesPanelContainer>
    );
};
